import { FormProvider, useForm } from 'react-hook-form';
import { Typography, Box } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';

import Modal from 'src/components/customModal';
import modals from 'src/constants/modals';
import { styledMainTitle } from 'src/components/InviteModal/styles';
import { ActionButtons } from 'src/layouts/main/styles';
import { handleCloseModal } from 'src/utils/propertyHelpers';
import { RHFAutocomplete } from 'src/components/hook-form';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { selectModal } from 'src/redux/slices/modal';
import { Quote } from 'src/redux/types/quoteSlice';
import Iconify from 'src/components/Iconify';
import LoadingButton from 'src/components/LoadingButton';
import deleteQuote from 'src/api/quote/delete-quote';
import { getQuotesThunk } from 'src/redux/slices/quoteSlice';
import { dispatch } from 'src/redux/store';
import updateQuote from 'src/api/quote/update-quote';
import { UpdateQuoteForm } from './types';
import { validationSchema } from './validation';
import { quoteStatusOptions } from '../../Quotes/constants';
import UsersList from './components/UsersList';

const Content = () => {
	const { enqueueSnackbar } = useSnackbar();
	const { payload } = useSelector(selectModal<Quote>);
	const [isLoading, setIsLoading] = useState(false);
	const form = useForm<UpdateQuoteForm>({
		resolver: yupResolver(validationSchema),
		mode: 'onChange',
		criteriaMode: 'all',
		defaultValues: {
			status: payload.status,
			clients: payload.clients,
			intermediaries: payload.intermediaries,
			referrers: payload.referrers ?? [],
		},
	});

	const handleSubmit = async (data: UpdateQuoteForm) => {
		try {
			setIsLoading(true);

			await updateQuote({ ...data, id: payload.id });

			dispatch(getQuotesThunk({ withLoading: true }));

			enqueueSnackbar('Quote successfully updated!');

			handleCloseModal();
		} catch (e) {
			if (e instanceof Error) enqueueSnackbar(e.message, { variant: 'error' });
		} finally {
			setIsLoading(false);
		}
	};

	const handleDelete = async () => {
		try {
			setIsLoading(true);

			await deleteQuote(payload.id);

			dispatch(getQuotesThunk({ withLoading: true }));

			handleCloseModal();
		} catch (e) {
			if (e instanceof Error) enqueueSnackbar(e.message, { variant: 'error' });
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<FormProvider {...form}>
			<form
				onSubmit={(e) => {
					e.stopPropagation();
					form.handleSubmit(handleSubmit)(e);
				}}
			>
				<Box position='relative'>
					<Typography sx={styledMainTitle} pt={0}>
						EDIT QUOTE
					</Typography>
					<Box display='flex' flexDirection='column' gap='15px'>
						<RHFAutocomplete
							name='status'
							label='Select quote status'
							options={quoteStatusOptions}
							valueAttribute='id'
						/>
						<UsersList name='clients' label='Clients' />
						<UsersList name='intermediaries' label='Intermediaries' />
						<UsersList name='referrers' label='Referrers' />
					</Box>
					<LoadingButton
						variant='contained'
						size='large'
						fullWidth
						loading={isLoading}
						color='error'
						sx={{ width: '30px', position: 'absolute', top: '-20px', right: 0 }}
						confirmText='Are you sure you want to delete this quote?'
						onClick={handleDelete}
					>
						<Iconify icon='mi:delete' fontSize='23px' />
					</LoadingButton>
					<ActionButtons mt={'20px'}>
						<LoadingButton variant='outlined' size='large' fullWidth onClick={handleCloseModal} loading={isLoading}>
							Cancel
						</LoadingButton>
						<LoadingButton variant='contained' size='large' fullWidth type='submit' loading={isLoading}>
							Update
						</LoadingButton>
					</ActionButtons>
				</Box>
			</form>
		</FormProvider>
	);
};

const UpdateQuoteModal = () => (
	<Modal name={modals.updateQuote} cardSx={{ overflow: 'visible', maxWidth: '500px' }}>
		<Content />
	</Modal>
);

export default UpdateQuoteModal;
